import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { SAR } from "../components/RiyalIcon";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import {
  ArrowLeft,
  Car,
  Map,
  MapPinned,
  Navigation,
  X,
} from "lucide-react";
import { saveActiveOrder } from "../lib/activeOrder";
import { DeliveryLocationPicker } from "../components/DeliveryLocationPicker";
import useRestaurantBrowserIdentity from "../hooks/useRestaurantBrowserIdentity";
import { Drawer, DrawerClose, DrawerContent } from "../components/ui/drawer";
import {
  getPublicRestaurant,
  getPublicRestaurantByBranchCode,
} from "../lib/publicRestaurantResource";

const PICKUP_STORE_ICON_URL = "/pickup-store-icon.png";
const PICKUP_CAR_ICON_URL = "/pickup-car-icon.png";
const PICKUP_DIRECTIONS_ICON_URL = "/pickup-directions.png";

export default function Checkout() {
  const { slug, branchCode } = useParams();
  const nav = useNavigate();
  const cart = useCart();
  const fulfillmentMethod = cart.fulfillment_method;
  const isStoreOrder = fulfillmentMethod !== "DELIVERY";
  const hasMatchingRouteBranchContext = Boolean(
    branchCode
    && cart.branch_code === branchCode
    && cart.branch_id,
  );
  const branchId = hasMatchingRouteBranchContext || !branchCode
    ? cart.branch_id || null
    : null;
  const mustResolveRouteBranch = Boolean(
    isStoreOrder
    && branchCode
    && !hasMatchingRouteBranchContext,
  );
  const [restaurant, setRestaurant] = useState(null);
  const [branches, setBranches] = useState([]);
  const [carPickupActive, setCarPickupActive] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(branchId);
  const [pickupBranchLocation, setPickupBranchLocation] = useState(null);
  const [directionsSheetOpen, setDirectionsSheetOpen] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "",
    car_type: "", car_color: "", car_plate: "",
    delivery_address: "", delivery_notes: "",
    delivery_latitude: cart.delivery_location?.latitude ?? null,
    delivery_longitude: cart.delivery_location?.longitude ?? null,
  });
  const [submitting, setSubmitting] = useState(false);
  const publicBranchCode = branchCode || restaurant?.branch_code;
  const publicBranchBase = (
    `/r/${slug}${publicBranchCode ? `/${encodeURIComponent(publicBranchCode)}` : ""}`
  );

  useRestaurantBrowserIdentity(restaurant);

  useEffect(() => {
    const restaurantRequest = mustResolveRouteBranch
      ? getPublicRestaurantByBranchCode(slug, branchCode)
      : getPublicRestaurant(slug, branchId);

    restaurantRequest.then((data) => {
      setRestaurant(data.restaurant);
      setBranches(data.branches || []);
      if (mustResolveRouteBranch) {
        setSelectedBranchId(data.restaurant.branch_id || null);
      }
      setCarPickupActive(false);
    });
  }, [branchCode, branchId, mustResolveRouteBranch, slug]);

  useEffect(() => {
    if (!cart.delivery_location) return;
    setForm((state) => ({
      ...state,
      delivery_latitude: cart.delivery_location.latitude,
      delivery_longitude: cart.delivery_location.longitude,
      delivery_address: cart.delivery_location.address || state.delivery_address,
    }));
  }, [cart.delivery_location]);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const method = fulfillmentMethod === "DELIVERY" ? "DELIVERY" : "STORE";
  const hasDeliveryLocation = Number.isFinite(form.delivery_latitude)
    && Number.isFinite(form.delivery_longitude);
  const pickupMethod = method === "STORE" && carPickupActive ? "CAR" : method;

  const pickupBranches = branches;
  const selectedBranch = branches.find((branch) => branch.id === selectedBranchId);
  const selectedBranchSupportsCarPickup = !!selectedBranch?.car_pickup_enabled;
  const pickupLatitude = Number(
    pickupBranchLocation?.latitude ?? selectedBranch?.latitude ?? restaurant?.latitude,
  );
  const pickupLongitude = Number(
    pickupBranchLocation?.longitude ?? selectedBranch?.longitude ?? restaurant?.longitude,
  );
  const hasPickupLocation = (
    Number.isFinite(pickupLatitude)
    && Number.isFinite(pickupLongitude)
  );
  const pickupMapPosition = hasPickupLocation
    ? [pickupLatitude, pickupLongitude]
    : [24.7136, 46.6753];
  const pickupMapsQuery = hasPickupLocation
    ? `${pickupLatitude},${pickupLongitude}`
    : selectedBranch?.address || restaurant?.address || "";
  const pickupMapsUrl = pickupMapsQuery
    ? "https://www.google.com/maps/search/?api=1&query="
      + encodeURIComponent(pickupMapsQuery)
    : "";
  const pickupMarkerLabel = restaurant?.name || "المطعم";
  const navigationDestination = `${pickupMapPosition[0]},${pickupMapPosition[1]}`;
  const encodedNavigationDestination = encodeURIComponent(navigationDestination);
  const googleMapsWebUrl = (
    "https://www.google.com/maps/dir/?api=1&destination="
    + encodedNavigationDestination
  );
  const appleMapsWebUrl = (
    "https://maps.apple.com/?daddr="
    + encodedNavigationDestination
    + "&dirflg=d"
  );

  useEffect(() => {
    const branchLatitude = Number(selectedBranch?.latitude);
    const branchLongitude = Number(selectedBranch?.longitude);
    const branchHasCoordinates = (
      Number.isFinite(branchLatitude)
      && Number.isFinite(branchLongitude)
    );

    if (branchHasCoordinates) {
      setPickupBranchLocation({
        latitude: branchLatitude,
        longitude: branchLongitude,
      });
      return undefined;
    }

    if (!selectedBranchId) {
      setPickupBranchLocation(null);
      return undefined;
    }

    let cancelled = false;

    getPublicRestaurant(slug, selectedBranchId).then((data) => {
      const branchRestaurant = data.restaurant;
      const latitude = Number(branchRestaurant?.latitude);
      const longitude = Number(branchRestaurant?.longitude);

      if (!cancelled) {
        setPickupBranchLocation(
          Number.isFinite(latitude) && Number.isFinite(longitude)
            ? { latitude, longitude }
            : null,
        );
      }
    }).catch(() => {
      if (!cancelled) {
        setPickupBranchLocation(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [selectedBranch?.latitude, selectedBranch?.longitude, selectedBranchId, slug]);

  const selectPickupBranch = (nextBranchId) => {
    setSelectedBranchId(nextBranchId);
    const nextBranch = branches.find((branch) => branch.id === nextBranchId);
    if (!nextBranch?.car_pickup_enabled) {
      setCarPickupActive(false);
    }
  };

  const openPickupDirectionsSheet = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDirectionsSheetOpen(true);
    event.currentTarget.blur();
  };

  const launchNavigation = (provider) => {
    const userAgent = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    const webUrl = provider === "google" ? googleMapsWebUrl : appleMapsWebUrl;

    setDirectionsSheetOpen(false);

    let targetUrl = null;
    if (provider === "google") {
      if (isAndroid) {
        targetUrl = `intent://maps.google.com/maps?daddr=${encodedNavigationDestination}&dirflg=d`
          + "#Intent;scheme=https;package=com.google.android.apps.maps;"
          + `S.browser_fallback_url=${encodeURIComponent(webUrl)};end`;
      } else if (isIOS) {
        const nativeGoogleMapsUrl = (
          "comgooglemaps://?daddr="
          + encodedNavigationDestination
          + "&directionsmode=driving"
        );
        try {
          window.location.href = nativeGoogleMapsUrl;
        } catch {
          window.location.href = webUrl;
        }
        return;
      }
    } else if (isIOS) {
      targetUrl = `https://maps.apple.com/?daddr=${encodedNavigationDestination}&dirflg=d`;
    }

    if (!targetUrl) {
      window.open(webUrl, "_blank", "noopener,noreferrer");
      return;
    }
    window.location.href = targetUrl;
  };

  useEffect(() => {
    if (method !== "STORE") return;
    const preferredBranchId = branchId || cart.branch_id || null;
    const currentBranchIsEligible = pickupBranches.some(
      (branch) => branch.id === selectedBranchId,
    );
    if (currentBranchIsEligible) return;
    const preferredBranchIsEligible = pickupBranches.some(
      (branch) => branch.id === preferredBranchId,
    );
    setSelectedBranchId(
      preferredBranchIsEligible ? preferredBranchId : pickupBranches[0]?.id || null,
    );
  }, [branchId, cart.branch_id, method, pickupBranches, selectedBranchId]);

  const submit = async () => {
    if (!/^05\d{8}$/.test(form.customer_phone)) return toast.error("رقم الجوال غير صحيح");
    if (pickupMethod === "CAR" && (!form.car_type || !form.car_color || !form.car_plate)) return toast.error("أكمل بيانات السيارة");
    if (method === "DELIVERY" && !hasDeliveryLocation) return toast.error("حدد موقع التوصيل من الخريطة");
    setSubmitting(true);
    try {
      const payload = {
        restaurant_id: cart.restaurant_id,
        items: cart.items.map(i => ({ product_id: i.product_id, quantity: i.quantity, selected_addons: i.selected_addons || [] })),
        pickup_method: pickupMethod,
        payment_method: "ON_PICKUP",
        source: "ONLINE",
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        notes: cart.notes || "",
      };
      if (selectedBranchId && pickupMethod !== "DELIVERY") {
        payload.branch_id = selectedBranchId;
      }
      if (pickupMethod === "CAR") {
        Object.assign(payload, {
          car_type: form.car_type,
          car_color: form.car_color,
          car_plate: form.car_plate,
        });
      }
      if (method === "DELIVERY") {
        Object.assign(payload, {
          delivery_address: form.delivery_address,
          delivery_notes: form.delivery_notes,
          delivery_latitude: form.delivery_latitude,
          delivery_longitude: form.delivery_longitude,
        });
      }
      const { data } = await api.post("/public/orders", payload);
      // Persist the active-order pointer under the restaurant slug so the customer
      // can return to it from the menu until it hits a terminal status.
      saveActiveOrder(slug, data);
      cart.clear();
      nav(`/order/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div
      className={
        "min-h-screen bg-white pb-[calc(11rem+env(safe-area-inset-bottom))] "
        + "md:pb-32"
      }
    >
      <header
        className={
          "max-w-md mx-auto flex items-center gap-3 px-5 pb-4 "
          + "pt-[calc(env(safe-area-inset-top)+1rem)] md:pt-4"
        }
      >
        <button
          onClick={() => nav(`${publicBranchBase}/cart`)}
          className="w-10 h-10 rounded-full bg-white grid place-items-center"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} className="rotate-180"/>
        </button>
        <h1 className="text-2xl font-extrabold">إتمام الطلب</h1>
      </header>

      <div
        className={
          "max-w-md mx-auto px-5 space-y-5 "
          + (method === "STORE" && pickupBranches.length > 1 ? "pt-2" : "")
        }
      >
        {method === "STORE" && pickupBranches.length > 1 && (
          <section
            className="space-y-0 overflow-hidden rounded-2xl border border-gray-200"
            data-testid="pickup-branch-selector"
          >
            <div className="grid grid-cols-2 items-center" dir="ltr">
              {pickupMapsUrl ? (
                <button
                  type="button"
                  onClick={openPickupDirectionsSheet}
                  onPointerDown={(event) => event.stopPropagation()}
                  className={
                    "flex h-[72px] w-full translate-y-1 items-center justify-start pl-2 "
                    + "outline-none transition-opacity duration-200 active:opacity-80 "
                    + "focus:outline-none focus:ring-0 focus-visible:outline-none "
                    + "focus-visible:ring-2 focus-visible:ring-orange-500"
                  }
                  data-testid="checkout-pickup-directions-link"
                  dir="ltr"
                  aria-label="الاتجاهات"
                >
                  <img
                    src={PICKUP_DIRECTIONS_ICON_URL}
                    alt="الاتجاهات"
                    className="h-[72px] w-[72px] object-contain"
                    data-testid="checkout-pickup-directions-image"
                  />
                </button>
              ) : (
                <div
                  className="flex h-12 items-center gap-1.5 text-sm font-bold text-gray-400"
                  data-testid="checkout-pickup-directions-unavailable"
                  dir="rtl"
                >
                  <Navigation size={17} strokeWidth={2.25} />
                  الاتجاهات
                </div>
              )}
              <label
                className="flex items-center pr-2 pl-5 text-sm font-bold text-gray-700"
                dir="rtl"
                aria-label="فرع الاستلام"
              >
                <select
                  value={selectedBranchId || ""}
                  onChange={(event) => selectPickupBranch(event.target.value)}
                  className={
                    "h-12 w-full rounded-2xl border border-gray-200 bg-white px-4 "
                    + "text-sm font-bold outline-none focus:border-orange-500"
                  }
                  data-testid="pickup-branch-select"
                >
                  {pickupBranches.map((branch) => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div
              className="relative h-[100px] overflow-hidden"
              data-testid="checkout-pickup-static-map"
            >
              <MapContainer
                center={pickupMapPosition}
                zoom={hasPickupLocation ? 15 : 11}
                className="h-full w-full"
                dragging={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                touchZoom={false}
                boxZoom={false}
                keyboard={false}
                zoomControl={false}
                attributionControl={false}
              >
                <PickupMapViewport
                  latitude={pickupMapPosition[0]}
                  longitude={pickupMapPosition[1]}
                  zoom={hasPickupLocation ? 15 : 11}
                />
                <TileLayer
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={19}
                  attribution="&copy; OpenStreetMap contributors"
                />
              </MapContainer>
              <div
                className={
                  "pointer-events-none absolute left-1/2 top-1/2 z-[1000] -translate-x-1/2 "
                  + "-translate-y-full"
                }
                data-testid="checkout-pickup-map-pin"
                data-latitude={pickupMapPosition[0]}
                data-longitude={pickupMapPosition[1]}
                aria-label={`موقع ${pickupMarkerLabel}`}
              >
                <div
                  className={
                    "flex max-w-[150px] items-center gap-1.5 rounded-full bg-black px-2 py-1 "
                    + "text-xs font-bold text-white"
                  }
                  data-testid="checkout-pickup-map-marker-label"
                  dir="rtl"
                >
                  {restaurant?.logo_url && (
                    <span
                      className="h-5 w-5 shrink-0 overflow-hidden rounded-full"
                      data-testid="checkout-pickup-map-marker-logo-container"
                    >
                      <img
                        src={restaurant.logo_url}
                        alt=""
                        className="h-full w-full object-cover"
                        data-testid="checkout-pickup-map-marker-logo"
                        aria-hidden="true"
                      />
                    </span>
                  )}
                  <span className="translate-y-px truncate">{pickupMarkerLabel}</span>
                </div>
                <div
                  className="mx-auto h-0 w-0 border-x-[6px] border-t-[7px] border-x-transparent border-t-black"
                  data-testid="checkout-pickup-map-marker-tail"
                  aria-hidden="true"
                />
              </div>
            </div>
          </section>
        )}

        {method === "STORE" && selectedBranch && (
          <section className="pt-1" data-testid="pickup-method-selector">
            <div
              className="mb-1 text-lg font-bold text-gray-700"
              data-testid="pickup-methods-title"
            >
              خيارات الاستلام
            </div>
            <div
              className="overflow-hidden rounded-[8px] border border-gray-200"
              role="radiogroup"
              aria-label="طريقة استلام الطلب"
            >
              <button
                type="button"
                onClick={() => setCarPickupActive(false)}
                className={
                  "flex h-[55px] w-full items-center justify-between px-4 text-sm "
                  + "font-bold text-black transition-colors duration-200"
                }
                data-testid="pickup-method-STORE"
                role="radio"
                aria-checked={!carPickupActive}
              >
                <span className="flex items-center gap-2" dir="rtl">
                  <img
                    src={PICKUP_STORE_ICON_URL}
                    alt=""
                    className="h-7 w-7 shrink-0 border-0 bg-transparent object-contain"
                    data-testid="pickup-method-STORE-icon"
                  />
                  <span>الاستلام من الفرع</span>
                </span>
                <span
                  className={
                    "h-[20px] w-[20px] shrink-0 rounded-full "
                    + (!carPickupActive
                      ? "border-[6px] border-black bg-white"
                      : "border-2 border-gray-400 bg-white")
                  }
                  style={{
                    backgroundColor: "#FFFFFF",
                    borderColor: !carPickupActive ? "#000000" : "#9CA3AF",
                    borderWidth: !carPickupActive ? "6px" : "2px",
                  }}
                  data-testid="pickup-method-STORE-dot"
                />
              </button>
              {selectedBranchSupportsCarPickup && (
                <>
                  <div
                    className="mx-[5px] h-px bg-gray-200"
                    data-testid="pickup-options-divider"
                    aria-hidden="true"
                  />
                  <div data-testid="pickup-car-option-container">
                    <button
                      type="button"
                      onClick={() => setCarPickupActive(true)}
                      className={
                        "flex h-[55px] w-full items-center justify-between px-4 text-sm "
                        + "font-bold text-black transition-colors duration-200"
                      }
                      data-testid="method-CAR"
                      role="radio"
                      aria-checked={carPickupActive}
                    >
                      <span className="flex items-center gap-2" dir="rtl">
                        <img
                          src={PICKUP_CAR_ICON_URL}
                          alt=""
                          className="h-7 w-7 shrink-0 border-0 bg-transparent object-contain"
                          data-testid="pickup-method-CAR-icon"
                        />
                        <span>استلام عند السيارة</span>
                      </span>
                      <span
                        className={
                          "h-[20px] w-[20px] shrink-0 rounded-full "
                          + (carPickupActive
                            ? "border-[6px] border-black bg-white"
                            : "border-2 border-gray-400 bg-white")
                        }
                        style={{
                          backgroundColor: "#FFFFFF",
                          borderColor: carPickupActive ? "#000000" : "#9CA3AF",
                          borderWidth: carPickupActive ? "6px" : "2px",
                        }}
                        data-testid="pickup-method-CAR-dot"
                      />
                    </button>
                    <div
                      className={
                        "grid overflow-hidden transition-[grid-template-rows] duration-200 "
                        + "ease-out "
                        + (carPickupActive ? "grid-rows-[1fr]" : "grid-rows-[0fr]")
                      }
                      data-testid="car-pickup-fields-panel"
                      aria-hidden={!carPickupActive}
                    >
                      <div className="min-h-0">
                        <div className="mt-3 space-y-2 px-4 pb-3">
                          <Field
                            label="نوع السيارة"
                            value={form.car_type}
                            onChange={v => update("car_type", v)}
                            placeholder="كامري"
                            testid="car-type"
                            compact
                            inline
                          />
                          <Field
                            label="اللون"
                            value={form.car_color}
                            onChange={v => update("car_color", v)}
                            placeholder="أبيض"
                            testid="car-color"
                            compact
                            inline
                          />
                          <Field
                            label="رقم اللوحة"
                            value={form.car_plate}
                            onChange={v => update("car_plate", v)}
                            placeholder="1234"
                            testid="car-plate"
                            compact
                            inline
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {!selectedBranchSupportsCarPickup && (
              <div
                className="mt-[6px] px-4 text-xs font-normal text-gray-500"
                data-testid="car-pickup-unavailable-message"
              >
                الاستلام عند السيارة غير متاح في هذا الفرع
              </div>
            )}
          </section>
        )}

        {method === "DELIVERY" && (
          <section className="space-y-2">
            <DeliveryLocationPicker
              latitude={form.delivery_latitude}
              longitude={form.delivery_longitude}
              title="اختر موقع التوصيل"
              showLocationHint
              onLocationChange={({ latitude, longitude }) => {
                setForm((state) => ({
                  ...state,
                  delivery_latitude: latitude,
                  delivery_longitude: longitude,
                }));
              }}
              onAddressDetected={(deliveryAddress) => {
                setForm((state) => ({
                  ...state,
                  delivery_address: deliveryAddress,
                }));
              }}
            />
          </section>
        )}

        <section className="space-y-2">
          <div
            className="overflow-hidden rounded-[8px] border border-gray-200 bg-white"
            data-testid="customer-details-fields"
          >
            <div className="pr-4 pl-2">
              <Field
                label="الاسم (اختياري)"
                value={form.customer_name}
                onChange={v => update("customer_name", v)}
                testid="customer-name"
                inline
                grouped
              />
            </div>
            <div
              className="h-px w-full bg-gray-200"
              data-testid="customer-details-fields-divider"
              aria-hidden="true"
            />
            <div className="pr-4 pl-2">
              <Field
                label="رقم الجوال"
                value={form.customer_phone}
                onChange={v => update("customer_phone", v)}
                placeholder="05XXXXXXXX"
                testid="customer-phone"
                inline
                grouped
              />
            </div>
          </div>
        </section>

        <section className="bg-white border border-orange-100 rounded-2xl p-4">
          <div className="text-sm font-bold text-gray-700 mb-1">طريقة الدفع</div>
          <div className="flex items-center gap-2 text-orange-700 font-bold">💵 الدفع عند الاستلام</div>
        </section>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-30">
        <div className="max-w-md mx-auto p-4">
          <div className="bg-white rounded-2xl card-shadow border border-orange-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-600">الإجمالي</span>
              <span className="text-2xl font-extrabold text-orange-600"><SAR>{cart.total + (method === "DELIVERY" ? Number(restaurant?.delivery_fee || 0) : 0)}</SAR></span>
            </div>
            <button disabled={submitting} onClick={submit} className="w-full pill brand-grad text-white font-bold text-base py-3.5 tap-target disabled:opacity-60" data-testid="confirm-order">
              {submitting ? "جاري التأكيد…" : "تأكيد الطلب"}
            </button>
          </div>
        </div>
      </div>
      <Drawer open={directionsSheetOpen} onOpenChange={setDirectionsSheetOpen}>
        <DrawerContent
          className="border-gray-200 bg-white"
          data-testid="directions-app-sheet"
          dir="rtl"
        >
          <div className="px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-4">
            <div className="flex items-center justify-between">
              <h2
                className="text-lg font-bold text-gray-900"
                data-testid="directions-app-sheet-title"
              >
                اختر تطبيق الخرائط
              </h2>
              <DrawerClose asChild>
                <button
                  type="button"
                  className={
                    "grid h-9 w-9 place-items-center rounded-full text-gray-500 "
                    + "transition-colors duration-200 hover:bg-gray-100"
                  }
                  data-testid="directions-app-sheet-close"
                  aria-label="إغلاق"
                >
                  <X size={18} />
                </button>
              </DrawerClose>
            </div>
            <div className="mt-5 overflow-hidden rounded-xl border border-gray-200">
              <button
                type="button"
                onClick={() => launchNavigation("google")}
                className={
                  "flex h-14 w-full items-center justify-between px-4 text-right "
                  + "transition-colors duration-200 hover:bg-gray-50"
                }
                data-testid="directions-google-maps-button"
                data-native-url={
                  `google.navigation:q=${navigationDestination}`
                }
                data-web-url={googleMapsWebUrl}
              >
                <span className="flex items-center gap-3">
                  <span
                    className="grid h-9 w-9 place-items-center rounded-full bg-blue-50"
                    data-testid="directions-google-maps-icon"
                  >
                    <MapPinned size={20} className="text-blue-600" />
                  </span>
                  <span className="text-sm font-bold text-gray-900">Google Maps</span>
                </span>
                <ArrowLeft size={18} className="text-gray-400" />
              </button>
              <div
                className="h-px w-full bg-gray-200"
                data-testid="directions-apps-divider"
                aria-hidden="true"
              />
              <button
                type="button"
                onClick={() => launchNavigation("apple")}
                className={
                  "flex h-14 w-full items-center justify-between px-4 text-right "
                  + "transition-colors duration-200 hover:bg-gray-50"
                }
                data-testid="directions-apple-maps-button"
                data-native-url={
                  `maps://?daddr=${encodedNavigationDestination}&dirflg=d`
                }
                data-web-url={appleMapsWebUrl}
              >
                <span className="flex items-center gap-3">
                  <span
                    className="grid h-9 w-9 place-items-center rounded-full bg-gray-100"
                    data-testid="directions-apple-maps-icon"
                  >
                    <Map size={20} className="text-gray-900" />
                  </span>
                  <span className="text-sm font-bold text-gray-900">Apple Maps</span>
                </span>
                <ArrowLeft size={18} className="text-gray-400" />
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function PickupMapViewport({ latitude, longitude, zoom }) {
  const map = useMap();

  useEffect(() => {
    map.setView([latitude, longitude], zoom, { animate: false });
    map.getContainer().dataset.mapCenterLatitude = String(latitude);
    map.getContainer().dataset.mapCenterLongitude = String(longitude);
  }, [latitude, longitude, map, zoom]);

  return null;
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  testid,
  compact = false,
  grouped = false,
  inline = false,
}) {
  return (
    <label
      className={
        inline
          ? "flex w-full items-center gap-3"
          : "block"
      }
    >
      <span
        className={
          `${grouped ? "text-black leading-none" : "text-gray-600"} text-xs font-bold `
          + (inline ? "w-24 shrink-0 text-right" : "")
        }
      >
        {label}
      </span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={
          `${inline ? "min-w-0 flex-1" : "mt-1 w-full"} `
          + `${compact ? "h-[42px]" : "h-12"} `
          + (grouped
            ? "border-0 bg-transparent px-0 py-0 text-left leading-none outline-none "
              + "focus:border-0 focus:ring-0"
            : "rounded-2xl bg-white border border-gray-200 px-4 outline-none "
              + "focus:border-orange-500 focus:ring-2 focus:ring-orange-100")
        }
      />
    </label>
  );
}
